/**
 * InstallPwa — a card that lets users install HACCP AU as a native-feeling
 * standalone app. Handles three browser realities at once:
 *
 *   • Chrome / Edge (Android, desktop): captures the `beforeinstallprompt`
 *     event and exposes an "Install app" button that triggers the native
 *     prompt.
 *   • iOS Safari: cannot trigger a prompt programmatically — shows the
 *     Add-to-Home-Screen steps with the share-icon glyph.
 *   • Already installed: detects `display-mode: standalone` and just shows
 *     a confirmation badge so we don't nag the user.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, Apple, CheckCircle2, Download, Share } from "lucide-react";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac with touch support
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS legacy flag
    window.navigator.standalone === true
  );
}

export default function InstallPwa() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const ios = isIOS();

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") {
      setInstalled(true);
    }
    setDeferredPrompt(null);
  };

  if (installed) {
    return (
      <div className="surface p-6 flex items-center gap-3" data-testid="pwa-installed">
        <CheckCircle2 className="w-6 h-6 text-eucalyptus-700" />
        <div>
          <div className="font-display font-bold">HACCP AU is installed</div>
          <p className="text-sm text-charcoal-soft">
            You&apos;re running the standalone app — long-press the home-screen icon to find the
            <strong> Scan &amp; log</strong> shortcut for one-tap temperature entry.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="surface p-6 space-y-4" data-testid="pwa-install-card">
      <div className="flex items-start gap-3">
        <Smartphone className="w-6 h-6 text-eucalyptus-700 mt-1" />
        <div>
          <div className="label-eyebrow">Mobile</div>
          <h3 className="font-display font-bold text-lg">Install HACCP AU on this device</h3>
          <p className="text-sm text-charcoal-soft mt-1">
            Pin the app to your phone or tablet&apos;s home screen for full-screen access and a
            one-tap <strong>Scan &amp; log</strong> shortcut from the icon menu.
          </p>
        </div>
      </div>

      {deferredPrompt && !ios && (
        <Button
          data-testid="pwa-install-btn"
          onClick={install}
          className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
        >
          <Download className="w-4 h-4" /> Install app
        </Button>
      )}

      {ios && (
        <div className="rounded-lg border border-[color:var(--border)] bg-bone-alt p-4 text-sm" data-testid="pwa-ios-instructions">
          <div className="flex items-center gap-2 mb-2 font-bold">
            <Apple className="w-4 h-4" /> iOS Safari
          </div>
          <ol className="space-y-1 pl-5 list-decimal text-charcoal-soft">
            <li className="flex items-center gap-1 flex-wrap">
              Tap the <Share className="inline w-4 h-4" /> Share button in Safari&apos;s toolbar.
            </li>
            <li>Scroll down and choose <strong>&ldquo;Add to Home Screen&rdquo;</strong>.</li>
            <li>Confirm the name <strong>HACCP AU</strong>, tap <strong>Add</strong>.</li>
            <li>Open from the home-screen icon — long-press it to find the <strong>Scan &amp; log</strong> shortcut.</li>
          </ol>
        </div>
      )}

      {!deferredPrompt && !ios && (
        <p className="text-xs text-charcoal-soft" data-testid="pwa-install-hint">
          The install prompt appears once the browser detects this is a returning, engaged user —
          check your browser&apos;s address bar for an install icon, or use the browser menu to
          &ldquo;Install HACCP AU&rdquo; / &ldquo;Add to home screen&rdquo;.
        </p>
      )}
    </div>
  );
}
